/**
 * TextAreaField - Multi-line text input
 *
 * Standardized textarea with consistent styling.
 */

import { useState, useCallback } from 'react';
import { FormField } from '../FormField';
import { formStyles, mergeStyles } from '../formStyles';

export interface TextAreaFieldProps {
  /** Field name attribute */
  name: string;
  /** Field label */
  label: string;
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Mark as required */
  required?: boolean;
  /** Error message */
  error?: string;
  /** Disable the field */
  disabled?: boolean;
  /** Number of visible rows */
  rows?: number;
  /** Minimum height */
  minHeight?: string;
  /** Maximum character length */
  maxLength?: number;
  /** Help text */
  hint?: string;
  /** Custom className */
  className?: string;
}

export function TextAreaField({
  name,
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  disabled,
  rows = 3,
  minHeight = '80px',
  maxLength,
  hint,
  className,
}: TextAreaFieldProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const textareaStyle = mergeStyles(
    formStyles.textarea,
    { minHeight },
    isFocused && formStyles.inputFocus,
    error && formStyles.inputError,
    disabled && formStyles.inputDisabled
  );

  const showCharCount = maxLength && value.length > maxLength * 0.8;

  return (
    <FormField label={label} required={required} error={error} hint={hint} className={className}>
      <textarea
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={textareaStyle}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {showCharCount && (
        <div
          style={{
            fontSize: '11px',
            color: value.length >= maxLength! ? '#ef4444' : 'var(--text-muted, #6b7280)',
            textAlign: 'right',
            marginTop: '2px',
          }}
        >
          {value.length}/{maxLength}
        </div>
      )}
    </FormField>
  );
}
